import React, { useEffect, useState } from 'react';
import {
  Card,
  Typography,
  Button,
  Space,
  message,
  Table,
  Spin,
  Row,
  Col,
} from 'antd';
import {
  ArrowLeftOutlined,
} from '@ant-design/icons';
import { useNavigate, useParams } from 'react-router-dom';
import { Invoice } from '../services/invoices';
import customerApi from '../services/customerApi';
import dayjs from 'dayjs';

const { Title, Text } = Typography;

export const CustomerInvoiceDetail: React.FC = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [invoice, setInvoice] = useState<Invoice | null>(null);

  useEffect(() => {
    if (id) {
      loadInvoice(parseInt(id, 10));
    }
  }, [id]);

  const loadInvoice = async (invoiceId: number) => {
    setLoading(true);
    try {
      const response = await customerApi.get(`/customer/invoices/${invoiceId}`);
      const loadedInvoice = response.data.data || response.data;
      setInvoice(loadedInvoice);
    } catch (error: any) {
      console.error('Failed to load invoice:', error);
      let errorMessage = 'Failed to load invoice';
      
      if (error.response) {
        errorMessage = error.response.data?.message || error.response.data?.error || `Server error: ${error.response.status}`;
        if (error.response.status === 401) {
          errorMessage = 'Unauthorized. Please login again.';
        } else if (error.response.status === 403) {
          errorMessage = 'Access denied.';
        } else if (error.response.status === 404) {
          errorMessage = 'Invoice not found.';
        }
      } else if (error.request) {
        errorMessage = 'Network error: Unable to connect to server';
      } else {
        errorMessage = error.message || 'Unknown error occurred';
      }
      
      message.error(errorMessage);
      navigate('/customer/billing');
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString: string) => {
    return dayjs(dateString).format('DD/MM/YYYY');
  };

  const getInvoiceDisplayName = () => {
    if (!invoice) return '';
    const customerType = 'Personal'; // In customer portal, invoice is always associated with a customer (personal)
    const firstInvoiceItem = invoice.items && invoice.items.length > 0 ? invoice.items[0] : null;
    // Use type assertion to access snake_case property from API response
    const taskType = firstInvoiceItem?.taskType || (firstInvoiceItem as any)?.task_type;
    return `${invoice.invoice_number} - ${customerType} - ${taskType?.name || 'Task'}`;
  };

  if (loading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
        <Spin size="large" />
      </div>
    );
  }

  if (!invoice) {
    return null;
  }

  // Get customer contact information
  const customerContact = invoice.customer?.contacts && invoice.customer.contacts.length > 0 
    ? invoice.customer.contacts[0] 
    : null;
  const addressText = customerContact 
    ? [customerContact.address1, customerContact.address2].filter(Boolean).join('\n')
    : '-';
  const contactInfo = customerContact 
    ? (customerContact.contact_number || customerContact.email || '-')
    : '-';

  return (
    <div style={{ padding: '24px' }}>
      <div style={{
        marginBottom: 24,
        paddingBottom: 16,
        borderBottom: '1px solid #f0f0f0',
      }}>
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
        }}>
          <Title level={2} style={{ margin: 0, color: '#1890ff', fontSize: 28, fontWeight: 600 }}>
            {getInvoiceDisplayName()}
          </Title>
          <Button
            icon={<ArrowLeftOutlined />}
            onClick={() => navigate('/customer/billing')}
          >
            Back
          </Button>
        </div>
      </div>

      <Row gutter={24}>
        {/* Left Column - Customer Information */}
        <Col span={12}>
          <Card title="Customer Information" style={{ marginBottom: 16 }} styles={{ body: { padding: 0 } }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <tbody>
                <tr style={{ borderBottom: '1px solid #f0f0f0' }}>
                  <td style={{ padding: '12px', width: '120px', verticalAlign: 'top', backgroundColor: '#fafafa' }}>
                    <strong>Quotation</strong>
                  </td>
                  <td colSpan={3} style={{ padding: '12px', verticalAlign: 'top' }}>
                    <div>{invoice.quotation?.quotation_number || '-'}</div>
                  </td>
                </tr>
                <tr style={{ borderBottom: '1px solid #f0f0f0' }}>
                  <td style={{ padding: '12px', width: '120px', verticalAlign: 'top', backgroundColor: '#fafafa' }}>
                    <strong>Customer</strong>
                  </td>
                  <td colSpan={3} style={{ padding: '12px', verticalAlign: 'top' }}>
                    <div>{invoice.customer?.name || '-'}</div>
                  </td>
                </tr>
                <tr style={{ borderBottom: '1px solid #f0f0f0' }}>
                  <td style={{ padding: '12px', width: '120px', verticalAlign: 'top', backgroundColor: '#fafafa' }}>
                    <strong>Address</strong>
                  </td>
                  <td colSpan={3} style={{ padding: '12px', verticalAlign: 'top' }}>
                    <div style={{ whiteSpace: 'pre-line' }}>{addressText}</div>
                  </td>
                </tr>
                <tr style={{ borderBottom: '1px solid #f0f0f0' }}>
                  <td style={{ padding: '12px', width: '120px', verticalAlign: 'top', backgroundColor: '#fafafa' }}>
                    <strong>Attention</strong>
                  </td>
                  <td colSpan={3} style={{ padding: '12px', verticalAlign: 'top' }}>
                    <div>{invoice.customer?.name || '-'}</div>
                  </td>
                </tr>
                <tr>
                  <td style={{ padding: '12px', width: '120px', verticalAlign: 'top', backgroundColor: '#fafafa' }}>
                    <strong>Contact</strong>
                  </td>
                  <td colSpan={3} style={{ padding: '12px', verticalAlign: 'top' }}>
                    <div>{contactInfo}</div>
                  </td>
                </tr>
              </tbody>
            </table>
          </Card>
        </Col>

        {/* Right Column - Invoice Details */}
        <Col span={12}>
          <Card title="Invoice Details" style={{ marginBottom: 16 }} styles={{ body: { padding: 0 } }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <tbody>
                <tr style={{ borderBottom: '1px solid #f0f0f0' }}>
                  <td style={{ padding: '12px', width: '120px', verticalAlign: 'top', backgroundColor: '#fafafa' }}>
                    <strong>Quotation No.</strong>
                  </td>
                  <td colSpan={3} style={{ padding: '12px', verticalAlign: 'top' }}>
                    <div>{invoice.quotation?.quotation_number || '-'}</div>
                  </td>
                </tr>
                <tr style={{ borderBottom: '1px solid #f0f0f0' }}>
                  <td style={{ padding: '12px', width: '120px', verticalAlign: 'top', backgroundColor: '#fafafa' }}>
                    <strong>Invoice No.</strong>
                  </td>
                  <td colSpan={3} style={{ padding: '12px', verticalAlign: 'top' }}>
                    <div>{invoice.invoice_number || '-'}</div>
                  </td>
                </tr>
                <tr style={{ borderBottom: '1px solid #f0f0f0' }}>
                  <td style={{ padding: '12px', width: '120px', verticalAlign: 'top', backgroundColor: '#fafafa' }}>
                    <strong>Date</strong>
                  </td>
                  <td colSpan={3} style={{ padding: '12px', verticalAlign: 'top' }}>
                    <div>{invoice.date ? formatDate(invoice.date) : '-'}</div>
                  </td>
                </tr>
                <tr style={{ borderBottom: '1px solid #f0f0f0' }}>
                  <td style={{ padding: '12px', width: '120px', verticalAlign: 'top', backgroundColor: '#fafafa' }}>
                    <strong>Advisor</strong>
                  </td>
                  <td colSpan={3} style={{ padding: '12px', verticalAlign: 'top' }}>
                    <div>{invoice.agent?.name || '-'}</div>
                  </td>
                </tr>
                <tr style={{ borderBottom: '1px solid #f0f0f0' }}>
                  <td style={{ padding: '12px', width: '120px', verticalAlign: 'top', backgroundColor: '#fafafa' }}>
                    <strong>Team</strong>
                  </td>
                  <td colSpan={3} style={{ padding: '12px', verticalAlign: 'top' }}>
                    <div>{invoice.team?.name || '-'}</div>
                  </td>
                </tr>
                <tr>
                  <td style={{ padding: '12px', width: '120px', verticalAlign: 'top', backgroundColor: '#fafafa' }}>
                    <strong>User</strong>
                  </td>
                  <td colSpan={3} style={{ padding: '12px', verticalAlign: 'top' }}>
                    <div>{invoice.staff?.name || '-'}</div>
                  </td>
                </tr>
              </tbody>
            </table>
          </Card>
        </Col>
      </Row>

      {/* Task Information */}
      <Card title="Task Information" style={{ marginBottom: 24 }} styles={{ body: { padding: '16px' } }}>
        <Table
          columns={[
            {
              title: 'Type',
              dataIndex: 'task_type_id',
              key: 'task_type_id',
              width: '15%',
              render: (_: any, record: any) => {
                const taskType = record.task_type || record.taskType; // Prioritize snake_case, fallback to camelCase
                return <span>{taskType?.name || '-'}</span>;
              },
            },
            {
              title: 'Year',
              dataIndex: 'year',
              key: 'year',
              width: '10%',
              render: (year: number) => <span>{year || '-'}</span>,
            },
            {
              title: 'Task No.',
              dataIndex: 'task_template_id',
              key: 'task_no',
              width: '12%',
              render: (_: any, record: any) => {
                const taskTemplate = record.task_template || record.taskTemplate; // Prioritize snake_case, fallback to camelCase
                return <span>{taskTemplate ? `Task #${taskTemplate.id}` : '-'}</span>;
              },
            },
            {
              title: 'Task',
              dataIndex: 'task_template_id',
              key: 'task_template_id',
              width: '18%',
              render: (_: any, record: any) => {
                const taskTemplate = record.task_template || record.taskTemplate; // Prioritize snake_case, fallback to camelCase
                return <span>{taskTemplate?.name || '-'}</span>;
              },
            },
            {
              title: 'Unit',
              dataIndex: 'unit',
              key: 'unit',
              width: '10%',
              render: (unit: number | string) => {
                const numValue = typeof unit === 'number' ? unit : parseFloat(unit || '0');
                return <span>{!isNaN(numValue) ? numValue.toFixed(2) : '-'}</span>;
              },
            },
            {
              title: 'Price/Unit',
              dataIndex: 'price_per_unit',
              key: 'price_per_unit',
              width: '12%',
              render: (pricePerUnit: number) => {
                const numValue = typeof pricePerUnit === 'number' ? pricePerUnit : parseFloat(pricePerUnit || '0');
                return <span>{!isNaN(numValue) ? numValue.toFixed(2) : '0.00'}</span>;
              },
            },
            {
              title: 'Discount',
              dataIndex: 'discount',
              key: 'discount',
              width: '10%',
              render: (discount: number) => {
                const numValue = typeof discount === 'number' ? discount : parseFloat(discount || '0');
                return <span>{!isNaN(numValue) ? numValue.toFixed(2) : '0.00'}</span>;
              },
            },
            {
              title: 'Amount',
              dataIndex: 'total_price',
              key: 'amount',
              width: '10%',
              render: (totalPrice: number) => {
                const numValue = typeof totalPrice === 'number' ? totalPrice : parseFloat(totalPrice || '0');
                return <span>{!isNaN(numValue) ? numValue.toFixed(2) : '0.00'}</span>;
              },
            },
          ]}
          dataSource={invoice.items?.map((item, index) => ({ ...item, key: index })) || []}
          pagination={false}
          size="small"
        />
      </Card>

      {/* Payments */}
      <Card title="Payments" style={{ marginBottom: 24 }} styles={{ body: { padding: '16px' } }}>
        <Table
          columns={[
            {
              title: 'Type',
              dataIndex: 'type',
              key: 'type',
              width: '20%',
              render: (type: string) => {
                return type === 'credit_note' ? 'Credit Note' : 'Official Receipt';
              },
            },
            {
              title: 'No',
              dataIndex: 'number',
              key: 'number',
              width: '20%',
              render: (number: string) => number || '-',
            },
            {
              title: 'Date',
              dataIndex: 'payment_date',
              key: 'payment_date',
              width: '20%',
              render: (date: string) => {
                if (!date) return '-';
                try {
                  return formatDate(date);
                } catch {
                  return date;
                }
              },
            },
            {
              title: 'Amount',
              dataIndex: 'payment_amount',
              key: 'payment_amount',
              width: '20%',
              render: (amount: number | string) => {
                const numAmount = typeof amount === 'string' ? parseFloat(amount) : amount;
                return `RM ${isNaN(numAmount) ? '0.00' : numAmount.toFixed(2)}`;
              },
            },
          ]}
          dataSource={invoice.payments?.map((item, index) => ({ ...item, key: item.id || index })) || []}
          pagination={false}
          size="small"
        />
      </Card>
    </div>
  );
};

